import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Pencil, X, Save, MessageSquare, Key, Copy, Check, Trash2, ShieldCheck, ShieldOff, Mail, UserPlus } from "lucide-react";

export default function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    const { data } = await api.get("/admin/customers");
    setCustomers(data);
    setLoading(false);
  };
  useEffect(() => { fetchAll(); }, []);

  const updateMarkup = async (uid, markup_percent) => {
    try {
      await api.put(`/admin/customers/${uid}`, { markup_percent: Number(markup_percent) || 0 });
      toast.success("Haszonkulcs mentve");
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const resetPassword = async (c) => {
    if (!window.confirm(`Új ideiglenes jelszót generál ${c.email} számára.\n\nA régi jelszó azonnal érvénytelenné válik. Folytatja?`)) return;
    try {
      const { data } = await api.post(`/admin/customers/${c.id}/reset-password`);
      setResetResult(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteCustomer = async (c) => {
    if (!window.confirm(`Biztosan törli a ${c.email} ügyfelet?\n\nA művelettel a hozzá tartozó ÖSSZES rendelés is törlődik. A művelet nem vonható vissza.`)) return;
    try {
      const { data } = await api.delete(`/admin/customers/${c.id}`);
      toast.success(`${c.email} törölve (${data.orders_deleted || 0} rendelés is)`);
      setCustomers((arr) => arr.filter((x) => x.id !== c.id));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const approveCustomer = async (c) => {
    const raw = window.prompt(
      `Adja meg az egyedi haszonkulcs % -ot ${c.email} számára (alapár × (1 + %/100) = ügyfél ára):`,
      String(c.markup_percent ?? 0)
    );
    if (raw == null) return; // cancelled
    const markup = Number(raw);
    if (isNaN(markup) || markup < -50 || markup > 200) {
      toast.error("Érvénytelen haszonkulcs (kb. -50 és 200 között).");
      return;
    }
    try {
      await api.post(`/admin/customers/${c.id}/approve`, { markup_percent: markup });
      toast.success(`${c.email} jóváhagyva (haszonkulcs: ${markup}%)`);
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const unapproveCustomer = async (c) => {
    if (!window.confirm(`Felfüggeszti ${c.email} hozzáférését?\n\nAz ügyfél bejelentkezhet, de nem fog árakat látni és nem tud rendelni.`)) return;
    try {
      await api.post(`/admin/customers/${c.id}/unapprove`);
      toast.success("Hozzáférés felfüggesztve");
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const exportEmails = async () => {
    try {
      const res = await api.get("/admin/customers/export-emails", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/plain" }));
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : `hirlevel_emailek_${new Date().toISOString().slice(0, 10)}.txt`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("E-mail lista letöltve — beilleszthető Outlook címzett mezőbe");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div>
      <div className="flex items-end justify-between gap-3 mb-2">
        <h1 className="text-2xl font-bold uppercase tracking-wide">Műhelyek</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowCreate((s) => !s)}
            className="btn-primary"
            data-testid="new-customer-toggle"
            title="Vevő manuális felvitele (pl. telefonon regisztráló műhely)"
          >
            <UserPlus size={14} /> Új vevő felvitele
          </button>
          <button
            onClick={exportEmails}
            className="btn-yellow"
            data-testid="export-emails-btn"
            title="Az összes regisztrált felhasználó e-mail címe pontosvesszővel elválasztva (mindegyik csak 1×) — hírlevélküldéshez, Outlookba másolható"
          >
            <Mail size={14} /> Hírlevél e-mail lista (.txt)
          </button>
        </div>
      </div>

      {showCreate && (
        <NewCustomerForm
          onCreated={(c) => { setCustomers((arr) => [c, ...arr]); setShowCreate(false); }}
          onClose={() => setShowCreate(false)}
        />
      )}
      <p className="text-sm mb-4" style={{ color: "var(--text-mute)" }}>
        Ügyféladatok szerkesztése, egyedi haszonkulcs (%) beállítása, és belső megjegyzések.
      </p>
      <div className="border-b-2 mb-5" style={{ borderColor: "var(--brand)" }} />

      <div className="card-elev overflow-hidden">
        <div className="section-title">Regisztrált műhelyek ({customers.length})</div>
        <div className="overflow-x-auto">
          <table className="table-b2b" data-testid="admin-customers-table">
            <thead>
              <tr>
                <th style={{ width: 90 }}>Státusz</th>
                <th>E-mail</th>
                <th>Név</th>
                <th>Műhely / Cég</th>
                <th>Adószám</th>
                <th>Telefon</th>
                <th>Megjegyzés</th>
                <th style={{ width: 170 }}>Haszonkulcs %</th>
                <th style={{ width: 170 }}>Műveletek</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="text-center py-8" style={{ color: "var(--text-mute)" }}>Betöltés...</td></tr>
              ) : customers.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-8" style={{ color: "var(--text-mute)" }}>Még nincs regisztrált műhely.</td></tr>
              ) : (
                customers.map((c) => (
                  <tr key={c.id} data-testid={`customer-row-${c.email}`} style={!c.approved ? { background: "#FFF8E1" } : {}}>
                    <td>
                      {c.approved ? (
                        <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded inline-flex items-center gap-1" style={{ background: "#E8F8E8", color: "#2A8C2A", border: "1px solid #2A8C2A" }} data-testid={`status-approved-${c.email}`}>
                          <ShieldCheck size={10} /> Aktív
                        </span>
                      ) : (
                        <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded inline-flex items-center gap-1" style={{ background: "#FFF4E0", color: "#DC8A00", border: "1px solid #DC8A00" }} data-testid={`status-pending-${c.email}`}>
                          <ShieldOff size={10} /> Várja a jóváhagyást
                        </span>
                      )}
                    </td>
                    <td><span className="font-mono text-xs" style={{ color: "var(--brand)" }}>{c.email}</span></td>
                    <td className="font-bold">{c.name}</td>
                    <td>{c.company || <span style={{ color: "var(--text-disabled)" }}>—</span>}</td>
                    <td className="font-mono text-xs">{c.tax_id || <span style={{ color: "var(--text-disabled)" }}>—</span>}</td>
                    <td className="font-mono text-xs">{c.phone || <span style={{ color: "var(--text-disabled)" }}>—</span>}</td>
                    <td className="text-xs max-w-[200px]" title={c.notes || ""}>
                      {c.notes ? (
                        <span className="inline-flex items-center gap-1">
                          <MessageSquare size={10} style={{ color: "var(--brand)" }} />
                          <span className="line-clamp-1">{c.notes}</span>
                        </span>
                      ) : (
                        <span style={{ color: "var(--text-disabled)" }}>—</span>
                      )}
                    </td>
                    <td>
                      <MarkupInput
                        defaultValue={c.markup_percent ?? 0}
                        onSave={(v) => updateMarkup(c.id, v)}
                        testid={`customer-markup-${c.email}`}
                      />
                    </td>
                    <td>
                      <div className="flex gap-1 flex-wrap">
                        {!c.approved ? (
                          <button
                            onClick={() => approveCustomer(c)}
                            className="btn-primary !py-1 !px-2 text-[10px] font-bold"
                            style={{ background: "linear-gradient(to bottom, #2A8C2A, #1F6F1F)" }}
                            data-testid={`customer-approve-${c.email}`}
                            title="Ügyfél jóváhagyása (haszonkulcs megadásával)"
                          >
                            <ShieldCheck size={11} /> Jóváhagy
                          </button>
                        ) : (
                          <button
                            onClick={() => unapproveCustomer(c)}
                            className="btn-ghost !py-1 !px-2 text-[10px]"
                            data-testid={`customer-unapprove-${c.email}`}
                            title="Hozzáférés felfüggesztése"
                          >
                            <ShieldOff size={11} />
                          </button>
                        )}
                        <button
                          onClick={() => setEditing(c)}
                          className="btn-ghost !py-1 !px-2 text-[11px]"
                          data-testid={`customer-edit-${c.email}`}
                          title="Szerkesztés"
                        >
                          <Pencil size={12} />
                        </button>
                        <button
                          onClick={() => resetPassword(c)}
                          className="btn-yellow !py-1 !px-2 text-[11px]"
                          data-testid={`customer-reset-pw-${c.email}`}
                          title="Új ideiglenes jelszó generálása"
                        >
                          <Key size={12} />
                        </button>
                        <button
                          onClick={() => deleteCustomer(c)}
                          className="btn-danger !py-1 !px-2 text-[11px]"
                          data-testid={`customer-delete-${c.email}`}
                          title="Műhely törlése"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <EditModal
          customer={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); fetchAll(); }}
        />
      )}

      {resetResult && (
        <ResetResultModal
          result={resetResult}
          onClose={() => setResetResult(null)}
        />
      )}
    </div>
  );
}

function ResetResultModal({ result, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(result.password);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
      data-testid="password-reset-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card-elev max-w-md w-full bg-white"
      >
        <div className="section-title justify-between" style={{ background: "linear-gradient(to bottom,#DC8A00,#B86F00)" }}>
          <span><Key size={14} className="inline mr-2" />Új ideiglenes jelszó generálva</span>
          <button type="button" onClick={onClose} style={{ color: "#fff" }}><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="text-xs p-3" style={{ background: "#FFF4E0", border: "1px solid #DC8A00", borderRadius: 4 }}>
            <strong>⚠ FIGYELEM:</strong> Ez a jelszó csak most látható egyszer. Másold ki és add át az ügyfélnek azonnal.
          </div>
          <div>
            <div className="text-xs font-bold uppercase mb-1" style={{ color: "var(--text-mute)" }}>E-mail (felhasználónév)</div>
            <div className="font-mono text-sm p-2" style={{ background: "var(--surface-elev)", border: "1px solid var(--border)", borderRadius: 3 }}>{result.email}</div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase mb-1" style={{ color: "var(--text-mute)" }}>Új jelszó</div>
            <div className="flex gap-2">
              <div
                className="font-mono text-lg font-bold flex-1 p-2"
                style={{ background: "var(--surface-elev)", border: "2px solid var(--brand)", borderRadius: 3, color: "var(--brand)", letterSpacing: "0.05em" }}
                data-testid="generated-password"
              >
                {result.password}
              </div>
              <button onClick={copy} className="btn-primary" data-testid="copy-password-btn" title="Másolás">
                {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Másolva" : "Másol"}
              </button>
            </div>
          </div>
          <p className="text-[11px]" style={{ color: "var(--text-mute)" }}>
            Az ügyfél ezzel az új jelszóval be tud lépni. Ha módosítani szeretné, a bejelentkezés után saját maga is megteheti.
          </p>
          <div className="flex justify-end">
            <button onClick={onClose} className="btn-ghost" data-testid="close-reset-modal">Bezár</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function MarkupInput({ defaultValue, onSave, testid }) {
  const [val, setVal] = React.useState(defaultValue);
  const dirty = Number(val) !== Number(defaultValue);
  return (
    <div className="flex items-center gap-1">
      <input
        type="number"
        step="0.5"
        className="input !py-1 !px-2 text-xs"
        style={{ width: 80 }}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        data-testid={testid}
        title="Egyedi haszonkulcs az ügyfél árához. Az alapárra jön rá."
      />
      <span className="text-xs" style={{ color: "var(--text-mute)" }}>%</span>
      <button
        onClick={() => onSave(val)}
        disabled={!dirty}
        className="btn-primary !py-1 !px-2 text-[10px] disabled:opacity-30"
        data-testid={`${testid}-save`}
      >
        OK
      </button>
    </div>
  );
}

function EditModal({ customer, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: customer.name || "",
    company: customer.company || "",
    tax_id: customer.tax_id || "",
    street: customer.street || "",
    city: customer.city || "",
    postal_code: customer.postal_code || "",
    country: customer.country || "",
    phone: customer.phone || "",
    fax: customer.fax || "",
    markup_percent: customer.markup_percent ?? 0,
    markup_by_brand: customer.markup_by_brand || {},
    notes: customer.notes || "",
    password: "",
  });
  const [saving, setSaving] = useState(false);

  const setBrandMarkup = (brand, val) => {
    setForm((f) => {
      const next = { ...(f.markup_by_brand || {}) };
      if (val === "" || val == null) delete next[brand];
      else next[brand] = Number(val);
      return { ...f, markup_by_brand: next };
    });
  };

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      const body = {
        ...form,
        markup_percent: Number(form.markup_percent) || 0,
        markup_by_brand: form.markup_by_brand || {},
      };
      if (!body.password) delete body.password;
      await api.put(`/admin/customers/${customer.id}`, body);
      toast.success(form.password ? "Ügyfél adatok és új jelszó mentve" : "Ügyfél adatok mentve");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-auto p-4"
      style={{ background: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
      data-testid="customer-edit-modal"
    >
      <form
        onSubmit={save}
        onClick={(e) => e.stopPropagation()}
        className="card-elev mt-10 max-w-2xl w-full bg-white"
      >
        <div className="section-title justify-between">
          <span>Ügyfél szerkesztése · {customer.email}</span>
          <button type="button" onClick={onClose} style={{ color: "#fff" }}><X size={16} /></button>
        </div>
        <div className="p-5 grid sm:grid-cols-2 gap-3 text-sm">
          <Field label="Név" name="name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
          <Field label="Cégnév / Műhely" name="company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} />
          <Field label="Adószám" name="tax_id" value={form.tax_id} onChange={(v) => setForm({ ...form, tax_id: v })} />
          <Field label="Telefon" name="phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
          <Field label="Fax" name="fax" value={form.fax} onChange={(v) => setForm({ ...form, fax: v })} />
          <Field label="Haszonkulcs alapérték (%)" name="markup_percent" type="number" value={form.markup_percent} onChange={(v) => setForm({ ...form, markup_percent: v })} />
          <Field label="Utca, házszám" name="street" value={form.street} onChange={(v) => setForm({ ...form, street: v })} />
          <Field label="Város" name="city" value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
          <Field label="Irányítószám" name="postal_code" value={form.postal_code} onChange={(v) => setForm({ ...form, postal_code: v })} />
          <Field label="Ország" name="country" value={form.country} onChange={(v) => setForm({ ...form, country: v })} />

          <div className="sm:col-span-2 pt-2 mt-1 border-t" style={{ borderColor: "var(--border)" }}>
            <label className="text-xs font-bold uppercase block mb-2">Egyedi haszonkulcs MÁRKÁNKÉNT (%) <span className="font-normal" style={{ color: "var(--text-mute)" }}>— üres = az alapérték érvényes</span></label>
            <div className="grid sm:grid-cols-3 gap-2">
              {[
                { k: "audi", label: "Audi" },
                { k: "vw", label: "Volkswagen" },
                { k: "skoda", label: "Škoda" },
                { k: "seat", label: "SEAT" },
                { k: "bmw", label: "BMW" },
                { k: "mercedes", label: "Mercedes-Benz" },
              ].map(({ k, label }) => (
                <div key={k} className="flex items-center gap-2 p-2" style={{ background: "var(--surface-elev)", border: "1px solid var(--border)", borderRadius: 3 }}>
                  <label className="text-xs font-bold flex-1">{label}</label>
                  <input
                    type="number"
                    step="0.5"
                    className="input !py-1 !px-2 text-xs"
                    style={{ width: 80 }}
                    value={form.markup_by_brand[k] ?? ""}
                    placeholder={String(form.markup_percent || 0)}
                    onChange={(e) => setBrandMarkup(k, e.target.value)}
                    data-testid={`customer-markup-${k}`}
                  />
                  <span className="text-xs" style={{ color: "var(--text-mute)" }}>%</span>
                </div>
              ))}
            </div>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-bold uppercase block mb-1">Belső megjegyzés</label>
            <textarea
              className="input"
              rows={4}
              placeholder="Pl. fizetési kondíciók, kapcsolattartó, speciális kérések..."
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="customer-edit-notes"
            />
          </div>
          <div className="sm:col-span-2 pt-2 border-t" style={{ borderColor: "var(--border)" }}>
            <label className="text-xs font-bold uppercase block mb-1" style={{ color: "var(--out-stock)" }}>
              <Key size={11} className="inline mr-1" /> Új jelszó beállítása (csak ha módosítani szeretné)
            </label>
            <input
              type="text"
              className="input"
              placeholder="Hagyja üresen, ha nem akar jelszót változtatni"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              data-testid="customer-edit-password"
              autoComplete="new-password"
            />
            <div className="text-[10px] mt-1" style={{ color: "var(--text-mute)" }}>
              A megadott jelszó azonnal érvénybe lép. A régi jelszó érvénytelenné válik. Min. 4 karakter.
            </div>
          </div>
        </div>
        <div className="px-5 pb-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-ghost">Mégse</button>
          <button type="submit" disabled={saving} className="btn-primary" data-testid="customer-edit-save">
            <Save size={14} /> {saving ? "Mentés..." : "Mentés"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, name, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase block mb-1">{label}</label>
      <input
        name={name}
        type={type}
        className="input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`customer-edit-${name}`}
      />
    </div>
  );
}

// Manual customer creation form (admin) — e.g. workshops registering by phone.
// The account is approved immediately so the customer can shop right away.
function NewCustomerForm({ onCreated, onClose }) {
  const [form, setForm] = useState({
    name: "", email: "", password: "", company: "", tax_id: "",
    phone: "", street: "", city: "", postal_code: "", markup_percent: 0,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const genPassword = () => {
    const chars = "abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789";
    let pw = "";
    const buf = new Uint32Array(10);
    window.crypto.getRandomValues(buf);
    for (const n of buf) pw += chars[n % chars.length];
    set("password", pw);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("A név megadása kötelező."); return; }
    if (!form.email.trim()) { toast.error("Az e-mail cím megadása kötelező."); return; }
    if ((form.password || "").length < 6) { toast.error("A jelszó legalább 6 karakter legyen."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/customers", {
        ...form,
        markup_percent: Number(form.markup_percent) || 0,
      });
      toast.success(`Vevő felvéve és jóváhagyva: ${data.email} — add meg neki a jelszót!`);
      onCreated(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card-elev mb-5 overflow-hidden" data-testid="new-customer-form">
      <div className="section-title flex items-center justify-between" style={{ background: "linear-gradient(180deg, #0f766e 0%, #134e4a 100%)" }}>
        <span className="flex items-center gap-2"><UserPlus size={14} /> Új vevő felvitele (azonnal jóváhagyva)</span>
        <button onClick={onClose} style={{ color: "#fff" }} data-testid="new-customer-close"><X size={14} /></button>
      </div>
      <div className="p-4 bg-white">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <NcField label="Kapcsolattartó neve *">
            <input className="input" value={form.name} onChange={(e) => set("name", e.target.value)}
              placeholder="Pl. Kovács Béla" data-testid="nc-name" />
          </NcField>
          <NcField label="E-mail *">
            <input className="input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
              placeholder="muhely@example.hu" data-testid="nc-email" />
          </NcField>
          <NcField label="Jelszó * (min. 6 karakter)">
            <div className="flex gap-1">
              <input className="input font-mono" value={form.password} onChange={(e) => set("password", e.target.value)}
                data-testid="nc-password" />
              <button onClick={genPassword} className="btn-ghost !px-2 text-xs whitespace-nowrap" title="Véletlen jelszó generálása"
                data-testid="nc-genpw">
                <Key size={12} />
              </button>
            </div>
          </NcField>
          <NcField label="Cégnév">
            <input className="input" value={form.company} onChange={(e) => set("company", e.target.value)} data-testid="nc-company" />
          </NcField>
          <NcField label="Adószám">
            <input className="input" value={form.tax_id} onChange={(e) => set("tax_id", e.target.value)} data-testid="nc-taxid" />
          </NcField>
          <NcField label="Telefon">
            <input className="input" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="nc-phone" />
          </NcField>
          <NcField label="Irányítószám">
            <input className="input" value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} data-testid="nc-zip" />
          </NcField>
          <NcField label="Város">
            <input className="input" value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="nc-city" />
          </NcField>
          <NcField label="Utca, házszám">
            <input className="input" value={form.street} onChange={(e) => set("street", e.target.value)} data-testid="nc-street" />
          </NcField>
          <NcField label="Árrés % (alapértelmezett)">
            <input className="input font-mono" type="number" min="0" step="0.1" value={form.markup_percent}
              onChange={(e) => set("markup_percent", e.target.value)} data-testid="nc-markup" />
          </NcField>
        </div>
        <div className="flex items-center justify-end gap-2 mt-4">
          <button onClick={onClose} className="btn-ghost" disabled={saving}>Mégse</button>
          <button onClick={save} className="btn-yellow" disabled={saving} data-testid="nc-save">
            <Save size={14} /> {saving ? "Mentés..." : "Vevő felvitele"}
          </button>
        </div>
      </div>
    </div>
  );
}

function NcField({ label, children }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase block mb-1" style={{ color: "var(--text-mute)" }}>{label}</label>
      {children}
    </div>
  );
}
